import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mode-switch",
  role: "tablist",
  "aria-label": "Insertion mode"
}
const _hoisted_2 = ["aria-selected"]
const _hoisted_3 = ["aria-selected"]
const _hoisted_4 = ["open"]
const _hoisted_5 = { class: "advanced-body" }
const _hoisted_6 = { class: "display-toggle" }
const _hoisted_7 = { title: 'Inserts use the real tag names either way.' }
const _hoisted_8 = ["checked"]
const _hoisted_9 = {
  key: 0,
  class: "empty-hint"
}
const _hoisted_10 = { class: "field-label region-heading" }
const _hoisted_11 = ["title"]
const _hoisted_12 = { class: "region-card" }
const _hoisted_13 = { class: "chip-list" }
const _hoisted_14 = ["title", "onClick"]
const _hoisted_15 = ["title", "onClick"]
const _hoisted_16 = ["title", "onClick"]
const _hoisted_17 = { class: "display-toggle" }
const _hoisted_18 = { title: 'Inserts use the real tag names either way.' }
const _hoisted_19 = ["checked"]
const _hoisted_20 = { class: "chip-list" }
const _hoisted_21 = ["title", "onClick"]
const _hoisted_22 = {
  key: 1,
  class: "empty-hint"
}
const _hoisted_23 = {
  key: 2,
  class: "empty-hint"
}

import { computed, ref, watch } from 'vue';
import {
  insertField as insertFieldTag,
  insertColumn as insertColumnTag,
  insertRegion as insertRegionMarkers,
  insertWholeRegion as insertWholeRegionMarkers,
} from '@/services/wordInsertion';
import { toLabel } from '@/services/tagLabels';
import type { SupportedTags, SupportedTagGroup } from '@/types/SupportedTags';
import InsertTableWizard from './InsertTableWizard.vue';

// Persistent insertion mode. Table is the deliberate first-open default so
// non-technical authors see the guided flow; regulars who prefer per-tag
// chips get their last choice back on the next open.
type InsertMode = 'table' | 'tags';
const MODE_KEY = 'thinklodge.suite.insertMode';

const ADVANCED_KEY = 'thinklodge.suite.tablesAdvancedOpen';

const SHOW_LABELS_KEY = 'thinklodge.suite.showLabels';

const OPEN = '{' + '{';
const CLOSE = '}' + '}';


export default /*@__PURE__*/_defineComponent({
  __name: 'TagPicker',
  props: {
    tags: {}
  },
  emits: ["insert-status"],
  setup(__props: any, { emit: __emit }) {

function loadMode(): InsertMode {
  if (typeof localStorage === 'undefined') return 'tags';
  const raw = localStorage.getItem(MODE_KEY);
  return raw === 'table' ? 'table' : 'tags';
}

const mode = ref<InsertMode>(loadMode());

function setMode(next: InsertMode): void {
  mode.value = next;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(MODE_KEY, next);
  }
}

// Advanced section in Tables mode holds the manual chips (Whole region /
// Region markers / column chips). Closed by default so first-time users see
// the guided wizard first; state persists so power users don't have to
// re-expand every session.
function loadAdvancedOpen(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(ADVANCED_KEY) === 'true';
}

const advancedOpen = ref<boolean>(loadAdvancedOpen());

function onToggleAdvanced(e: Event): void {
  const open = (e.target as HTMLDetailsElement).open;
  advancedOpen.value = open;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ADVANCED_KEY, String(open));
  }
}

const props = __props;

const emit = __emit;

// Named handler for the wizard's @status event. An inline arrow in the
// template hides the parameter type from ts-loader's stricter inference
// (TS7006), even though vue-tsc accepts it. Naming it here forwards the
// string through the parent's insert-status emit.
function forwardWizardStatus(m: string): void {
  emit('insert-status', m);
}

// If the current source-type has no repeating regions the Table mode has
// nothing to offer — fall back to the chip picker so the pane isn't left
// showing an empty wizard. The user's saved preference is untouched; when
// they switch back to a source-type that has regions, Table returns.
watch(
  () => props.tags.groups.length,
  (n) => {
    if (n === 0 && mode.value === 'table') mode.value = 'tags';
  },
  { immediate: true },
);

// Search state — transient, per-open (not persisted). Kept in sync with the Suite
// TagReferencePanel.vue by the same rule the label derivation follows: same filter
// shape, duplicated across the two repos.
const search = ref('');

// Persist the toggle so a user's preference survives task-pane closes. Default
// to labels since raw CF_UPPER_SNAKE_CASE is noisy for authors who don't
// already know the tag inventory.
function loadShowLabels(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const raw = localStorage.getItem(SHOW_LABELS_KEY);
  return raw === null ? true : raw === 'true';
}

const showLabels = ref<boolean>(loadShowLabels());

function toggleShowLabels(next: boolean): void {
  showLabels.value = next;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(SHOW_LABELS_KEY, String(next));
  }
}

// String-concat the braces (not in the template) so the compiler doesn't try to
// interpret literal `{{` inside a mustache — same trick as the Suite frontend.
function rawField(tag: string): string {
  return `${OPEN}${tag}${CLOSE}`;
}

// Backend-provided label wins (single source of truth per #2390); falls back to the
// local toLabel() derivation when the DTO doesn't declare a label — either an older
// server without label support or a placeholder source-type whose mapper hasn't
// authored labels yet.
function labelForField(tag: string): string {
  return props.tags.fieldTagLabels?.[tag] ?? toLabel(tag);
}

function labelForColumn(col: string, groupName: string): string {
  return props.tags.groupColumnLabels?.[groupName]?.[col] ?? toLabel(col);
}

function labelForRegion(name: string): string {
  return props.tags.groupLabels?.[name] ?? toLabel(name);
}

// Chip surfaces one view; the tooltip surfaces the OTHER view so hovering is always
// informative. Labels-on → chip is friendly name, tooltip is the raw insert-ready string.
// Labels-off → chip is the raw insert-ready string, tooltip is the friendly name.
function displayForField(tag: string): string {
  return showLabels.value ? labelForField(tag) : rawField(tag);
}

function tooltipForField(tag: string): string {
  return showLabels.value ? rawField(tag) : labelForField(tag);
}

function displayForColumn(col: string, groupName: string): string {
  return showLabels.value ? labelForColumn(col, groupName) : `{{${col}}}`;
}

function tooltipForColumn(col: string, groupName: string): string {
  return showLabels.value ? `{{${col}}}` : labelForColumn(col, groupName);
}

function displayForRegion(name: string): string {
  return showLabels.value ? labelForRegion(name) : name;
}

function regionMarkerLabel(name: string): string {
  return showLabels.value
    ? `${labelForRegion(name)} table markers`
    : '{{TableStart / TableEnd}}';
}

function regionMarkerTooltip(name: string): string {
  return `{{TableStart:${name}}} … {{TableEnd:${name}}}`;
}

// Region hint moved into a hover-title on the info icon next to the region heading —
// saves a full paragraph of vertical space per region. Content: the same explanation
// used to render inline, plus a concrete example the author can imitate.
function regionHintTitle(group: SupportedTagGroup): string {
  const firstCol = group.columns[0] ?? 'Code';
  const secondCol = group.columns[1] ?? 'Name';
  return (
    'Wrap repeating rows between the start and end markers. Use column tags inside each row.\n\n'
    + 'Example:\n'
    + `{{TableStart:${group.name}}}\n`
    + `{{${firstCol}}} {{${secondCol}}}\n`
    + `{{TableEnd:${group.name}}}`
  );
}

// ---- Search filtering (duplicated in ServiceWarranty-UI TagReferencePanel.vue) --------
// Case-insensitive substring against raw tag AND display label. Sync rule per MEMORY.md:
// changes here must land on the Suite side in the same PR family to keep the two
// surfaces behaviorally identical.

function matches(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

const normalizedSearch = computed(() => search.value.trim());

function fieldTagMatches(tag: string): boolean {
  return matches(tag, normalizedSearch.value) || matches(labelForField(tag), normalizedSearch.value);
}

function regionMatches(group: SupportedTagGroup): boolean {
  return matches(group.name, normalizedSearch.value) || matches(labelForRegion(group.name), normalizedSearch.value);
}

function columnMatches(col: string, groupName: string): boolean {
  return matches(col, normalizedSearch.value) || matches(labelForColumn(col, groupName), normalizedSearch.value);
}

const filteredFieldTags = computed(() => {
  if (normalizedSearch.value === '') return props.tags.fieldTags;
  return props.tags.fieldTags.filter(fieldTagMatches);
});

const visibleGroups = computed(() => {
  if (normalizedSearch.value === '') return props.tags.groups;
  return props.tags.groups.filter(
    (group: SupportedTagGroup) =>
      regionMatches(group) || group.columns.some((c: string) => columnMatches(c, group.name)),
  );
});

function visibleColumnsFor(group: SupportedTagGroup): string[] {
  if (normalizedSearch.value === '' || regionMatches(group)) return [...group.columns];
  return group.columns.filter((c: string) => columnMatches(c, group.name));
}

// --------------------------------------------------------------------------------------

async function withStatus(label: string, action: () => Promise<void>): Promise<void> {
  try {
    await action();
    emit('insert-status', `Inserted ${label}`);
  } catch (e) {
    emit('insert-status', e instanceof Error ? `Failed: ${e.message}` : 'Insert failed');
  }
}

function onInsertField(tag: string): void {
  // Status message always uses the raw tag so the user has an audit trail
  // of exactly what landed in the document.
  void withStatus(rawField(tag), () => insertFieldTag(tag));
}

function onInsertColumn(col: string): void {
  void withStatus(`{{${col}}}`, () => insertColumnTag(col));
}

function onInsertRegion(group: string): void {
  void withStatus(
    `{{TableStart:${group}}} … {{TableEnd:${group}}}`,
    () => insertRegionMarkers(group),
  );
}

function wholeRegionPreview(group: SupportedTagGroup): string {
  const cols = group.columns.map((c: string) => `{{${c}}}`).join(' ');
  return `{{TableStart:${group.name}}}\n${cols}\n{{TableEnd:${group.name}}}`;
}

function onInsertWholeRegion(group: SupportedTagGroup): void {
  void withStatus(
    `whole region {{${group.name}}}`,
    () => insertWholeRegionMarkers(group.name, group.columns),
  );
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (__props.tags.groups.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("button", {
            type: "button",
            role: "tab",
            "aria-selected": mode.value === 'tags',
            class: _normalizeClass(['mode-switch-btn', { active: mode.value === 'tags' }]),
            title: "Insert one tag at a time",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (setMode('tags')))
          }, " Individual Tags ", 10 /* CLASS, PROPS */, _hoisted_2),
          _createElementVNode("button", {
            type: "button",
            role: "tab",
            "aria-selected": mode.value === 'table',
            class: _normalizeClass(['mode-switch-btn', { active: mode.value === 'table' }]),
            title: "Insert a real Word table for a repeating region",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (setMode('table')))
          }, " Tables ", 10 /* CLASS, PROPS */, _hoisted_3)
        ]))
      : _createCommentVNode("v-if", true),
    (mode.value === 'table' && __props.tags.groups.length > 0)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createVNode(InsertTableWizard, {
            tags: __props.tags,
            onInserted: _cache[2] || (_cache[2] = ($event: any) => (emit('insert-status', 'Table inserted'))),
            onStatus: forwardWizardStatus
          }, null, 8 /* PROPS */, ["tags"]),
          _createElementVNode("details", {
            class: "advanced",
            open: advancedOpen.value,
            onToggle: onToggleAdvanced
          }, [
            _cache[11] || (_cache[11] = _createElementVNode("summary", { class: "advanced-summary" }, [
              _createElementVNode("span", { class: "advanced-label" }, "Advanced"),
              _createElementVNode("span", { class: "advanced-hint" }, "Insert region markers or columns one at a time")
            ], -1 /* CACHED */)),
            _createElementVNode("div", _hoisted_5, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((search).value = $event)),
                type: "search",
                class: "search-input",
                placeholder: "Search regions and columns",
                "aria-label": "Search regions and columns"
              }, null, 512 /* NEED_PATCH */), [
                [
                  _vModelText,
                  search.value,
                  void 0,
                  { trim: true }
                ]
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("label", _hoisted_7, [
                  _createElementVNode("input", {
                    type: "checkbox",
                    checked: showLabels.value,
                    onChange: _cache[4] || (_cache[4] = ($event: any) => (toggleShowLabels(($event.target as HTMLInputElement).checked)))
                  }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_8),
                  _cache[7] || (_cache[7] = _createElementVNode("span", null, "Show labels", -1 /* CACHED */))
                ])
              ]),
              (visibleGroups.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, " No regions match \"" + _toDisplayString(search.value) + "\". ", 1 /* TEXT */))
                : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(visibleGroups.value, (group) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: group.name
                    }, [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("span", null, "Region: " + _toDisplayString(displayForRegion(group.name)), 1 /* TEXT */),
                        _createElementVNode("span", {
                          class: "info-icon",
                          title: regionHintTitle(group),
                          "aria-label": "How to use this region"
                        }, "ⓘ", 8 /* PROPS */, _hoisted_11)
                      ]),
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "chip whole-region",
                            title: wholeRegionPreview(group),
                            onClick: ($event: any) => (onInsertWholeRegion(group))
                          }, [...(_cache[8] || (_cache[8] = [
                            _createElementVNode("span", null, "Whole region", -1 /* CACHED */),
                            _createElementVNode("span", {
                              class: "icon",
                              "aria-hidden": "true"
                            }, "↵", -1 /* CACHED */)
                          ]))], 8 /* PROPS */, _hoisted_14),
                          _createElementVNode("button", {
                            type: "button",
                            class: "chip region",
                            title: regionMarkerTooltip(group.name),
                            onClick: ($event: any) => (onInsertRegion(group.name))
                          }, [
                            _createElementVNode("span", null, _toDisplayString(regionMarkerLabel(group.name)), 1 /* TEXT */),
                            _cache[9] || (_cache[9] = _createElementVNode("span", {
                              class: "icon",
                              "aria-hidden": "true"
                            }, "↵", -1 /* CACHED */))
                          ], 8 /* PROPS */, _hoisted_15),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleColumnsFor(group), (col) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: col,
                              type: "button",
                              class: "chip",
                              title: tooltipForColumn(col, group.name),
                              onClick: ($event: any) => (onInsertColumn(col))
                            }, [
                              _createElementVNode("span", null, _toDisplayString(displayForColumn(col, group.name)), 1 /* TEXT */),
                              _cache[10] || (_cache[10] = _createElementVNode("span", {
                                class: "icon",
                                "aria-hidden": "true"
                              }, "↵", -1 /* CACHED */))
                            ], 8 /* PROPS */, _hoisted_16))
                          }), 128 /* KEYED_FRAGMENT */))
                        ])
                      ])
                    ], 64 /* STABLE_FRAGMENT */))
                  }), 128 /* KEYED_FRAGMENT */))
            ])
          ], 40 /* PROPS, NEED_HYDRATION */, _hoisted_4)
        ], 64 /* STABLE_FRAGMENT */))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "section-header" }, [
            _createElementVNode("h3", { class: "section-title" }, "Insert Individual Tags"),
            _createElementVNode("p", { class: "section-desc" }, "Click a tag to insert it at the cursor.")
          ], -1 /* CACHED */)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((search).value = $event)),
            type: "search",
            class: "search-input",
            placeholder: "Search tags",
            "aria-label": "Search tags"
          }, null, 512 /* NEED_PATCH */), [
            [
              _vModelText,
              search.value,
              void 0,
              { trim: true }
            ]
          ]),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("label", _hoisted_18, [
              _createElementVNode("input", {
                type: "checkbox",
                checked: showLabels.value,
                onChange: _cache[6] || (_cache[6] = ($event: any) => (toggleShowLabels(($event.target as HTMLInputElement).checked)))
              }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_19),
              _cache[12] || (_cache[12] = _createElementVNode("span", null, "Show labels", -1 /* CACHED */))
            ])
          ]),
          (filteredFieldTags.value.length > 0)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _cache[14] || (_cache[14] = _createElementVNode("div", { class: "field-label" }, "Header tags", -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_20, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredFieldTags.value, (tag) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: tag,
                      type: "button",
                      class: "chip primary",
                      title: tooltipForField(tag),
                      onClick: ($event: any) => (onInsertField(tag))
                    }, [
                      _createElementVNode("span", null, _toDisplayString(displayForField(tag)), 1 /* TEXT */),
                      _cache[13] || (_cache[13] = _createElementVNode("span", {
                        class: "icon",
                        "aria-hidden": "true"
                      }, "↵", -1 /* CACHED */))
                    ], 8 /* PROPS */, _hoisted_21))
                  }), 128 /* KEYED_FRAGMENT */))
                ])
              ], 64 /* STABLE_FRAGMENT */))
            : (normalizedSearch.value !== '')
              ? (_openBlock(), _createElementBlock("div", _hoisted_22, " No tags match \"" + _toDisplayString(search.value) + "\". ", 1 /* TEXT */))
              : (_openBlock(), _createElementBlock("div", _hoisted_23, " This source type has no header tags. "))
        ], 64 /* STABLE_FRAGMENT */))
  ]))
}
}

})